import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useTheme } from '@cobalt/react-theme-provider';
import Flex from '@cobalt/react-flex';
import Box from '@cobalt/react-box';
import List, { ClickableItem } from '@cobalt/react-list';
import Icon from '@cobalt/react-icon';
import { Text } from '@cobalt/react-typography';

import { CaseStatusEnum, genCaseStatusMap } from '../helper';

const SHOWN_CASE_STATUS_LIST = [
  CaseStatusEnum.New,
  CaseStatusEnum.Open,
  CaseStatusEnum.Pending,
  CaseStatusEnum.OnHold,
  CaseStatusEnum.Resolved,
];

interface CaseStatusListorProps {
  status: CaseStatusEnum;
  isOpen: boolean;
  onClick: () => void;
  onChange: (status: CaseStatusEnum) => void;
}

const Listor = styled(Box)<{ visible: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  width: 140px;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.4);
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: all 0.2s ease-in-out 0s;
  z-index: 999;
`;

function CaseStatusListor(props: CaseStatusListorProps) {
  const { status, isOpen, onClick, onChange } = props;

  const [t] = useTranslation();
  const theme = useTheme();

  const caseStatusMap = useMemo(() => genCaseStatusMap(t), [t]);
  const caseStatusObj = useMemo(() => caseStatusMap[status], [caseStatusMap, status]);

  return (
    <Listor data-testid="caseStatusListor" padding="2" visible={isOpen} onClick={onClick}>
      <List>
        {SHOWN_CASE_STATUS_LIST.map((item) => {
          const { value: status } = caseStatusMap[item];

          return (
            <ClickableItem key={status} selected={status === caseStatusObj.value} onClick={() => onChange(status)}>
              <Flex alignY="center" padding="2">
                {/* Status icon */}
                <Flex
                  style={{ borderRadius: '50%' }}
                  alignX="center"
                  alignY="center"
                  width="16px"
                  height="16px"
                  backgroundColor={caseStatusMap[item].defaultBgc}
                >
                  <Icon
                    style={{ transform: 'scale(0.5) translateY(-1px)' }}
                    name={caseStatusMap[item].icon}
                    color="white"
                    size="small"
                  />
                </Flex>

                {/* Text */}
                <Box style={{ flex: 1, overflowX: 'hidden' }} paddingLeft="1">
                  <Text size="small" truncated color={theme.basic800}>
                    {caseStatusMap[item].text}
                  </Text>
                </Box>
              </Flex>
            </ClickableItem>
          );
        })}
      </List>
    </Listor>
  );
}

export default CaseStatusListor;
